import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Alert, InputGroup, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Logo from '../assets/logo_black.png';
import { auth, db, doc, getDoc, RecaptchaVerifier, signInWithPhoneNumber, onAuthStateChanged } from '..'; // Assuming Firebase is initialized in firebase.js

// Ensure that reCAPTCHA is initialized correctly for client-side only
if (typeof window !== 'undefined') {
  window.recaptchaVerifier = null;
}

const AdminLogin = () => {
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState(1);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false); // State for "Remember Me"
  const [passwordVisible, setPasswordVisible] = useState(false); // New state for visibility toggle
  const [isLoading, setIsLoading] = useState(false); // State for loading button
  const [isOtpLoading, setIsOtpLoading] = useState(false); // State for OTP button loading

  // Initialize reCAPTCHA verifier
  useEffect(() => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'invisible',
        callback: () => {
          console.log('reCAPTCHA verified');
        },
      });
    }

    const savedCredentials = JSON.parse(localStorage.getItem('adminCredentials'));
    if (savedCredentials && savedCredentials.phone && savedCredentials.password) {
      setPhone(savedCredentials.phone);
      setPassword(savedCredentials.password);
      setRememberMe(true); // Remember Me is checked
    }
    // Check if user data is saved in localStorage (for "Remember Me" functionality)
    const storedUser = JSON.parse(localStorage.getItem('loggedInUser'));
    if (storedUser) {
      setPhone(storedUser.phone || '');
      setPassword(storedUser.password || '');
    }

    const loggedInUser = localStorage.getItem('loggedInUser'); // Retrieve stored login state

    if (loggedInUser) {
      // If a logged-in user exists in localStorage, navigate directly to the dashboard
      navigate('/dashboard');
    } else {
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        if (user) {
          // Store user data to localStorage for persistence
          localStorage.setItem('loggedInUser', JSON.stringify(user));
          navigate('/dashboard'); // Redirect to dashboard if already logged in
        }
      });

      // Cleanup the listener when the component unmounts
      return () => unsubscribe();
    }
  }, [navigate]);

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!phone || !password) {
      setError('Please fill all fields');
      return;
    }

    setIsLoading(true); // Start loading

    try {
      // Query Firestore for admin security credentials (admin/security)
      const docRef = doc(db, "admin", "security"); // The document is `security` under `admin` collection
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const securityData = docSnap.data();

        // Ensure phone numbers are compared exactly
        const enteredPhone = phone.trim(); // Remove extra spaces
        const enteredPassword = password.trim(); // Remove extra spaces

        const storedPhone = securityData.phone.trim(); // Remove extra spaces in Firestore data
        const storedPassword = securityData.password.trim(); // Remove extra spaces in Firestore data

        // Check if the entered phone number exists in the Firestore security document
        if (storedPhone === enteredPhone) {
          // Check if the password matches
          if (storedPassword === enteredPassword) {
            // Set up reCAPTCHA for OTP
            const appVerifier = window.recaptchaVerifier;
            const confirmation = await signInWithPhoneNumber(auth, `+${enteredPhone.replace(/\D/g, '')}`, appVerifier);
            setConfirmationResult(confirmation);
            setStep(2);

            // Save credentials in localStorage if "Remember Me" is checked
            if (rememberMe) {
              localStorage.setItem('adminCredentials', JSON.stringify({ phone: enteredPhone, password: enteredPassword }));
            }
          } else {
            setError('Incorrect password. Please try again.');
          }
        } else {
          setError('Phone number not found. Please check your credentials.');
        }
      } else {
        setError('Security document not found. Please check your setup.');
      }
    } catch (err) {
      console.error('Error in login process:', err.message);
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false); // Stop loading after the process is finished
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    setIsOtpLoading(true); // Start loading for OTP button

    try {
      // Confirm OTP entered by the user
      await confirmationResult.confirm(otp);
      localStorage.setItem('loggedInUser', JSON.stringify({ phone, password })); // Store user data in localStorage
      navigate('/dashboard'); // Redirect to the dashboard after successful login
    } catch (err) {
      console.error('OTP verification error:', err.message);
      setError('Invalid OTP. Please try again.');
    } finally {
      setIsOtpLoading(false); // Stop loading after OTP submission process is finished
    }
  };

  return (
    <Container fluid className="vh-100 d-flex justify-content-center align-items-center" style={{ backgroundColor: '#8B0A6C' }}>
      <div id="recaptcha-container"></div>
      
      <Row className="w-100 justify-content-center">
        <Col xs={12} md={6} lg={4} className="p-4 border rounded shadow-sm bg-white">
          <div className="text-center mb-4">
            <img src={Logo} alt="Logo" className="mb-3" style={{ width: '80px' }} />
            <h3 className="fw-bold">Invoicely Pro</h3>
            <p className="text-muted">Manage Projects & Scope Extensions</p>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}

          {step === 1 && (
            <Form onSubmit={handleLoginSubmit}>
              <Form.Group controlId="formPhone" className="mb-3">
                <Form.Label>Phone Number</Form.Label>
                <Form.Control
                  type="tel"
                  placeholder="Example: 1234567890 (include country code)"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                />
              </Form.Group>

              <Form.Group controlId="formPassword" className="mb-3">
                <Form.Label>Password</Form.Label>
                <InputGroup>
                  <Form.Control
                    type={passwordVisible ? "text" : "password"}
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <InputGroup.Text 
                    style={{ cursor: 'pointer' }}
                    onClick={() => setPasswordVisible(!passwordVisible)}
                  >
                    {passwordVisible ? "Hide" : "Show"}
                  </InputGroup.Text>
                </InputGroup>
              </Form.Group>

              <Form.Group controlId="formRememberMe" className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Remember Me"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
              </Form.Group>

              <Button 
                type="submit" 
                className="w-100 mt-3" 
                variant="dark" 
                disabled={isLoading} // Disable button while loading
              >
                {isLoading ? (
                  <>
                    <Spinner animation="border" size="sm" /> Sending OTP...
                  </>
                ) : (
                  "Send OTP"
                )}
              </Button>
            </Form>
          )}

          {step === 2 && (
            <Form onSubmit={handleOtpSubmit}>
              <Form.Group controlId="formOtp" className="mb-3">
                <Form.Label>Enter OTP</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  required
                />
              </Form.Group>

              <Button 
                type="submit" 
                className="w-100 mt-3" 
                variant="dark" 
                disabled={isOtpLoading} // Disable button while OTP is being processed
              >
                {isOtpLoading ? (
                  <>
                    <Spinner animation="border" size="sm" /> Verifying...
                  </>
                ) : (
                  "Verify & Log In"
                )}
              </Button>
            </Form>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default AdminLogin;





// import React, { useState, useEffect } from 'react';
// import { Container, Row, Col, Form, Button, Alert } from 'react-bootstrap';
// import { useNavigate } from 'react-router-dom';
// import Logo from '../assets/logo_black.png';
// import { auth, db , doc, getDoc} from '..';
// import { RecaptchaVerifier, signInWithPhoneNumber,onAuthStateChanged } from '..';

// // Extend the Window interface to include recaptchaVerifier
// if (typeof window !== 'undefined') {
//   window.recaptchaVerifier = null;
// }

// const AdminLogin = () => {
//   const navigate = useNavigate();
//   const [phone, setPhone] = useState('+91');
//   const [password, setPassword] = useState('');
//   const [otp, setOtp] = useState('');
//   const [step, setStep] = useState(1);
//   const [confirmationResult, setConfirmationResult] = useState(null);
//   const [error, setError] = useState('');

//   // Initialize reCAPTCHA verifier
//   useEffect(() => {
//     const loggedInUser = localStorage.getItem('loggedInUser'); // Retrieve stored login state

//     if (loggedInUser) {
//       // If a logged-in user exists in localStorage, navigate directly to the dashboard
//       navigate('/dashboard');
//     } else {
//       const unsubscribe = onAuthStateChanged(auth, (user) => {
//         if (user) {
//           // Store user data to localStorage for persistence
//           localStorage.setItem('loggedInUser', JSON.stringify(user));
//           navigate('/dashboard'); // Redirect to dashboard if already logged in
//         }
//       });

//       // Cleanup the listener when the component unmounts
//       return () => unsubscribe();
//     }
//   }, [navigate]);


//   useEffect(() => {
//     if (window.recaptchaVerifier === null) {
//       window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
//         size: 'invisible',
//         callback: () => {
//           // This forces reCAPTCHA to render when needed
//         },
//       });
//     }
//   }, []);

//   const handleLoginSubmit = async (e) => {
//     e.preventDefault();
//     setError('');
  
//     if (!phone || !password) {
//       setError('Please fill all fields');
//       return;
//     }
  
//     try {
//       const formattedPhone = `+${phone.replace(/\D/g, '')}`; // Format phone number
//       const appVerifier = window.recaptchaVerifier;
  
//       const confirmation = await signInWithPhoneNumber(
//         auth,
//         formattedPhone,
//         appVerifier
//       );
//       setConfirmationResult(confirmation);
//       setStep(2);
//     } catch (err) {
//       setError('Error sending OTP. Please check your phone number.');
//       console.error('OTP send error:', err);
//     }
//   };

//   const handleOtpSubmit = async (e) => {
//     e.preventDefault();
//     setError('');

//     try {
//       // Confirm OTP entered by the user
//       await confirmationResult.confirm(otp);
      
//       // Query Firestore for admin security credentials (admin/security)
//       const docRef = doc(db, "admin", "security"); // The document is `security` under `admin` collection
//       const docSnap = await getDoc(docRef);

//       if (docSnap.exists()) {
//         const securityData = docSnap.data();
        
//         // Check if the phone and password match
//         if (securityData.phone === phone && securityData.password === password) {
//           localStorage.setItem('loggedInUser', JSON.stringify(securityData)); // Store user data
//           navigate('/dashboard'); // Redirect to the dashboard
//         } else {
//           setError('Incorrect phone number or password. Please try again.');
//         }
//       } else {
//         setError('Security document not found. Please check your setup.');
//       }
//     } catch (err) {
//       setError('Invalid OTP. Please try again.');
//       console.error('OTP verification error:', err);
//     }
//   };

//   return (
//     <Container fluid className="vh-100 d-flex justify-content-center align-items-center" style={{ backgroundColor: '#8B0A6C' }}>
//       <div id="recaptcha-container"></div>
      
//       <Row className="w-100 justify-content-center">
//         <Col xs={12} md={6} lg={4} className="p-4 border rounded shadow-sm bg-white">
//           <div className="text-center mb-4">
//             <img src={Logo} alt="Logo" className="mb-3" style={{ width: '80px' }} />
//             <h3 className="fw-bold">Invoicely Pro</h3>
//             <p className="text-muted">Manage Projects & Scope Extensions</p>
//           </div>

//           {error && <Alert variant="danger">{error}</Alert>}

//           {step === 1 && (
//             <Form onSubmit={handleLoginSubmit}>
//               <Form.Group controlId="formPhone" className="mb-3">
//                 <Form.Label>Phone Number</Form.Label>
//                 <Form.Control
//                   type="tel"
//                   placeholder="Example: 1234567890 (include country code)"
//                   value={phone}
//                   onChange={(e) => setPhone(e.target.value)}
//                   required
//                 />
//               </Form.Group>

//               <Form.Group controlId="formPassword" className="mb-3">
//                 <Form.Label>Password</Form.Label>
//                 <Form.Control
//                   type="password"
//                   placeholder="Enter password"
//                   value={password}
//                   onChange={(e) => setPassword(e.target.value)}
//                   required
//                 />
//               </Form.Group>

//               <Button type="submit" className="w-100 mt-3" variant="dark">
//                 Send OTP
//               </Button>
//             </Form>
//           )}

//           {step === 2 && (
//             <Form onSubmit={handleOtpSubmit}>
//               <Form.Group controlId="formOtp" className="mb-3">
//                 <Form.Label>Enter OTP</Form.Label>
//                 <Form.Control
//                   type="text"
//                   placeholder="Enter 6-digit code"
//                   value={otp}
//                   onChange={(e) => setOtp(e.target.value)}
//                   required
//                 />
//               </Form.Group>

//               <Button type="submit" className="w-100 mt-3" variant="dark">
//                 Verify & Log In
//               </Button>
//             </Form>
//           )}
//         </Col>
//       </Row>
//     </Container>
//   );
// };

// export default AdminLogin;



// import React, { useState, useEffect } from 'react';
// import { Container, Row, Col, Form, Button, Alert } from 'react-bootstrap';
// import { useNavigate } from 'react-router-dom';
// import Logo from '../assets/logo_black.png';
// import { auth } from '/Users/magnaopus/Desktop/invoicely-admin/src/firebase.js';
// import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';

// // Extend the Window interface to include recaptchaVerifier
// if (typeof window !== 'undefined') {
//   window.recaptchaVerifier = null;
// }

// const AdminLogin = () => {
//   const navigate = useNavigate();
//   const [phone, setPhone] = useState('+91');
//   const [password, setPassword] = useState('');
//   const [otp, setOtp] = useState('');
//   const [step, setStep] = useState(1);
//   const [confirmationResult, setConfirmationResult] = useState(null);
//   const [error, setError] = useState('');

//   // Initialize reCAPTCHA verifier
//   useEffect(() => {
//     window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
//       size: 'invisible',
//       callback: () => {
//         // This forces reCAPTCHA to render when needed
//       },
//     });
//   }, []);

//   const handleLoginSubmit = async (e) => {
//     e.preventDefault();
//     setError('');
  
//     if (!phone || !password) {
//       setError('Please fill all fields');
//       return;
//     }
  
//     try {
//       const formattedPhone = `+${phone.replace(/\D/g, '')}`; // Format phone number
//       const appVerifier = window.recaptchaVerifier;
  
//       const confirmation = await signInWithPhoneNumber(
//         auth,
//         formattedPhone,
//         appVerifier
//       );
//       setConfirmationResult(confirmation);
//       setStep(2);
//     } catch (err) {
//       setError('Error sending OTP. Please check your phone number.');
//       console.error('OTP send error:', err);
//     }
//   };

//   const handleOtpSubmit = async (e) => {
//     e.preventDefault();
//     setError('');

//     try {
//       await confirmationResult.confirm(otp);
//       navigate('/dashboard');
//     } catch (err) {
//       setError('Invalid OTP. Please try again.');
//       console.error('OTP verification error:', err);
//     }
//   };

//   return (
//     <Container fluid className="vh-100 d-flex justify-content-center align-items-center" style={{ backgroundColor: '#8B0A6C' }}>
//       <div id="recaptcha-container"></div>
      
//       <Row className="w-100 justify-content-center">
//         <Col xs={12} md={6} lg={4} className="p-4 border rounded shadow-sm bg-white">
//           <div className="text-center mb-4">
//             <img src={Logo} alt="Logo" className="mb-3" style={{ width: '80px' }} />
//             <h3 className="fw-bold">Invoicely Pro</h3>
//             <p className="text-muted">Manage Projects & Scope Extensions</p>
//           </div>

//           {error && <Alert variant="danger">{error}</Alert>}

//           {step === 1 && (
//             <Form onSubmit={handleLoginSubmit}>
//               <Form.Group controlId="formPhone" className="mb-3">
//                 <Form.Label>Phone Number</Form.Label>
//                 <Form.Control
//                   type="tel"
//                   placeholder="Example: 1234567890 (include country code)"
//                   value={phone}
//                   onChange={(e) => setPhone(e.target.value)}
//                   required
//                 />
//               </Form.Group>

//               <Form.Group controlId="formPassword" className="mb-3">
//                 <Form.Label>Password</Form.Label>
//                 <Form.Control
//                   type="password"
//                   placeholder="Enter password"
//                   value={password}
//                   onChange={(e) => setPassword(e.target.value)}
//                   required
//                 />
//               </Form.Group>

//               <Button type="submit" className="w-100 mt-3" variant="dark">
//                 Send OTP
//               </Button>
//             </Form>
//           )}

//           {step === 2 && (
//             <Form onSubmit={handleOtpSubmit}>
//               <Form.Group controlId="formOtp" className="mb-3">
//                 <Form.Label>Enter OTP</Form.Label>
//                 <Form.Control
//                   type="text"
//                   placeholder="Enter 6-digit code"
//                   value={otp}
//                   onChange={(e) => setOtp(e.target.value)}
//                   required
//                 />
//               </Form.Group>

//               <Button type="submit" className="w-100 mt-3" variant="dark">
//                 Verify & Log In
//               </Button>
//             </Form>
//           )}
//         </Col>
//       </Row>
//     </Container>
//   );
// };

// export default AdminLogin;
